/**
 * Create Connection Modal
 *
 * Modern, clean modal for creating new data connector configurations.
 * Follows the same design patterns as the New Analysis modal.
 */

import React, { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { ConnectorType, SyncMode } from '../../generated/connectors/connectors';
import {
  createConnectorConfigurationApiConnectorsConfigurationsPost,
  useCreateConnectorConfigurationApiConnectorsConfigurationsPost as useCreateConnector,
  useTestConnectionApiConnectorsTestConnectionPost as useTestConnection,
  useEstimateSyncCostApiConnectorsEstimateCostPost as useEstimateCost,
} from '../../generated/data-connectors/data-connectors';
import { googleWorkspaceAuthorizeOauthAuthorizeGoogleGet } from '../../generated/oauth/oauth';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query-keys';
import { useToasts } from '../../stores/useToasts';
import { Tooltip } from '../common/Tooltip';
import {
  getConnectorCatalogEntry,
} from './connectorCatalog';

interface CreateConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultConnectorType?: string;
}

interface ConnectionFormData {
  connector_name: string;
  connector_type: string;
  api_key: string;
  erp_api_secret: string;
  erp_base_url: string;
  erp_company_id: string;
  erp_mariadb_host: string;
  erp_mariadb_port: string;
  erp_mariadb_database: string;
  erp_mariadb_user: string;
  erp_mariadb_password: string;
  erp_selected_tables_json: string;
  access_key_id: string;
  secret_access_key: string;
  redshift_host: string;
  redshift_port: string;
  redshift_database: string;
  redshift_db_user: string;
  redshift_region: string;
  redshift_schema: string;
  redshift_is_serverless: boolean;
  redshift_workgroup: string;
  // Snowflake
  snowflake_account: string;
  snowflake_username: string;
  snowflake_password: string;
  snowflake_database: string;
  snowflake_warehouse: string;
  snowflake_role: string;
  snowflake_schema: string;
  postgres_host: string;
  postgres_port: string;
  postgres_database: string;
  postgres_username: string;
  postgres_password: string;
  postgres_schema: string;
  postgres_sslmode: string;
  postgres_secret_id: string;
  postgres_region: string;
  mariadb_host: string;
  mariadb_port: string;
  mariadb_database: string;
  mariadb_username: string;
  mariadb_password: string;
  mariadb_include_table_patterns: string;
  mariadb_exclude_table_patterns: string;
  bigquery_project_id: string;
  bigquery_client_id: string;
  bigquery_client_secret: string;
  bigquery_redirect_uri: string;
  search_query: Record<string, any>;
  sync_config?: Record<string, any>;
  sync_mode: SyncMode;
  sync_schedule?: string;
  description?: string;
}

const ERP_ANALYTICS_SOURCE_MODE_KEY = 'analytics_source_mode';
const ERP_ANALYTICS_SOURCE_MODES = {
  API: 'api',
  MARIADB: 'mariadb',
} as const;
const ERP_API_PREVIEW_MAX_RECORDS_KEY = 'api_preview_max_records';
type ERPAnalyticsSourceMode =
  (typeof ERP_ANALYTICS_SOURCE_MODES)[keyof typeof ERP_ANALYTICS_SOURCE_MODES];

// Best-effort: detect whether a host is a Redshift Serverless endpoint so
// the form can default the Serverless toggle correctly.
function isServerlessHost(host: string): boolean {
  return /redshift-serverless\.amazonaws\.com/i.test(host || '');
}

function isGoogleOAuthConnector(connectorType: string): boolean {
  return (
    connectorType === ConnectorType.GOOGLE_DRIVE ||
    connectorType === ConnectorType.GOOGLE_SHEETS ||
    connectorType === ConnectorType.GMAIL
  );
}

function hasGoogleTargetConfig(formData: ConnectionFormData): boolean {
  if (formData.connector_type === ConnectorType.GOOGLE_DRIVE) {
    return Boolean(
      String(formData.sync_config?.shared_folder_link || '').trim() ||
        String(formData.sync_config?.folder_id || '').trim() ||
        String(formData.sync_config?.shared_drive_link || '').trim() ||
        String(formData.sync_config?.shared_drive_id || '').trim(),
    );
  }
  if (formData.connector_type === ConnectorType.GOOGLE_SHEETS) {
    return Boolean(
      String(formData.sync_config?.spreadsheet_link || '').trim() ||
        String(formData.sync_config?.spreadsheet_id || '').trim(),
    );
  }
  return formData.connector_type === ConnectorType.GMAIL;
}

function parseERPSelectedTablesJson(raw: string): { selectedTables?: any[]; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { error: 'Selected ERP DocTypes or tables are required for Agent Mesh analytics.' };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      return { error: 'Selected ERP DocTypes or tables must be a JSON array.' };
    }
    if (parsed.length === 0) {
      return { error: 'Select at least one ERP DocType or table for Agent Mesh analytics.' };
    }
    return { selectedTables: parsed };
  } catch {
    return { error: 'Selected ERP DocTypes or tables must be valid JSON.' };
  }
}

function getERPAnalyticsSourceMode(formData: ConnectionFormData): ERPAnalyticsSourceMode {
  return formData.sync_config?.[ERP_ANALYTICS_SOURCE_MODE_KEY] === ERP_ANALYTICS_SOURCE_MODES.MARIADB
    ? ERP_ANALYTICS_SOURCE_MODES.MARIADB
    : ERP_ANALYTICS_SOURCE_MODES.API;
}

function buildERPAnalyticsCredentials(formData: ConnectionFormData): Record<string, string> {
  return {
    mariadb_host: formData.erp_mariadb_host.trim(),
    mariadb_port: formData.erp_mariadb_port.trim() || '3306',
    mariadb_database: formData.erp_mariadb_database.trim(),
    mariadb_user: formData.erp_mariadb_user.trim(),
    mariadb_password: formData.erp_mariadb_password,
  };
}

function missingERPAnalyticsCredential(formData: ConnectionFormData): boolean {
  return Boolean(
    !formData.erp_mariadb_host.trim() ||
      !formData.erp_mariadb_database.trim() ||
      !formData.erp_mariadb_user.trim() ||
      !formData.erp_mariadb_password,
  );
}

function buildERPSyncConfig(
  formData: ConnectionFormData,
  selectedTables?: any[],
): Record<string, any> {
  const sourceMode = getERPAnalyticsSourceMode(formData);
  const {
    [ERP_API_PREVIEW_MAX_RECORDS_KEY]: _apiPreviewMaxRecords,
    max_records: _legacyMaxRecords,
    ...persistedSyncConfig
  } = formData.sync_config || {};

  return {
    ...persistedSyncConfig,
    provider: formData.sync_config?.provider || 'erpnext',
    [ERP_ANALYTICS_SOURCE_MODE_KEY]: sourceMode,
    base_url: formData.erp_base_url.trim(),
    company_id: formData.erp_company_id.trim(),
    analytics_target: {
      ...(formData.sync_config?.analytics_target || {}),
      schema: 'erp_analytics',
    },
    ...(selectedTables?.length ? { selected_tables: selectedTables } : {}),
  };
}

function parseMariaDBPatternText(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function buildMariaDBSyncConfig(formData: ConnectionFormData): Record<string, any> {
  const includeTablePatterns = parseMariaDBPatternText(formData.mariadb_include_table_patterns);
  const excludeTablePatterns = parseMariaDBPatternText(formData.mariadb_exclude_table_patterns);
  const tableDiscovery =
    includeTablePatterns.length || excludeTablePatterns.length ? 'filtered' : 'all';

  return {
    ...formData.sync_config,
    database: formData.mariadb_database.trim(),
    table_discovery: tableDiscovery,
    ...(includeTablePatterns.length ? { include_table_patterns: includeTablePatterns } : {}),
    ...(excludeTablePatterns.length ? { exclude_table_patterns: excludeTablePatterns } : {}),
  };
}

const CONNECTOR_CARD_ORDER = [
  ConnectorType.PEOPLE_DATA_LABS,
  ConnectorType.GREENHOUSE,
  ConnectorType.HUBSPOT,
  ConnectorType.OPERATING,
  ConnectorType.FATHOM,
  ConnectorType.SLACK,
  ConnectorType.GOOGLE_DRIVE,
  ConnectorType.GOOGLE_SHEETS,
  ConnectorType.GMAIL,
  ConnectorType.GRANOLA,
  ConnectorType.S3,
  ConnectorType.REDSHIFT,
  ConnectorType.POSTGRES,
  ConnectorType.MARIADB,
  ConnectorType.ERP,
  ConnectorType.BIGQUERY,
  ConnectorType.SNOWFLAKE,
  ConnectorType.ATLAN,
  ConnectorType.FILESYSTEM,
];

const PDL_QUERY_TEMPLATES = {
  empty: {},
  software_engineers: {
    job_title: ['software engineer', 'backend engineer', 'frontend engineer'],
    location_name: 'United States',
  },
  senior_leaders: {
    job_title_role: 'executive',
    job_title_levels: ['director', 'vp', 'c-suite'],
  },
};

const ERP_PROVIDER_OPTIONS = [
  { value: 'erpnext', label: 'ERPNext' },
];

const buildBaseSyncConfig = () => ({
  connection_purpose: 'data_source',
  scope_type: 'tenant',
  scope_target: '',
  required_permissions: [] as string[],
  use_for_tenant_storage: false,
});

const buildInitialSyncConfig = (connectorType: string): Record<string, any> => {
  const baseSyncConfig = buildBaseSyncConfig();

  if (connectorType === ConnectorType.FILESYSTEM) {
    return {
      ...baseSyncConfig,
      directory_path: '',
      file_extensions: ['.pdf', '.docx', '.txt'],
      recursive: false,
    };
  }

  if (connectorType === ConnectorType.S3) {
    return {
      ...baseSyncConfig,
      backend: 's3',
      bucket: '',
      region: 'us-east-1',
      endpoint_url: '',
      path_prefix: '',
      force_path_style: false,
      default_local_source_path: '',
    };
  }

  if (connectorType === ConnectorType.REDSHIFT) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'applet',
      scope_target: 'cashflow',
    };
  }

  if (connectorType === ConnectorType.GRANOLA) {
    return {
      ...baseSyncConfig,
      live_retrieval_only: true,
    };
  }

  if (connectorType === ConnectorType.BIGQUERY) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'applet',
      scope_target: 'structured_data',
    };
  }

  if (connectorType === ConnectorType.SNOWFLAKE) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'applet',
      scope_target: 'structured_data',
    };
  }

  if (connectorType === ConnectorType.POSTGRES) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'applet',
      scope_target: 'structured_data',
      schema: 'public',
      sslmode: 'require',
    };
  }

  if (connectorType === ConnectorType.MARIADB) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'applet',
      scope_target: 'structured_data',
      database: '',
    };
  }

  if (connectorType === ConnectorType.ERP) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'data_source',
      scope_type: 'tenant',
      scope_target: '',
      provider: 'erpnext',
      [ERP_ANALYTICS_SOURCE_MODE_KEY]: ERP_ANALYTICS_SOURCE_MODES.API,
      base_url: '',
      company_id: '',
      analytics_target: { schema: 'erp_analytics' },
      [ERP_API_PREVIEW_MAX_RECORDS_KEY]: 100,
    };
  }

  if (connectorType === ConnectorType.ATLAN) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'catalog',
      scope_type: 'tenant',
    };
  }

  if (connectorType === ConnectorType.GOOGLE_DRIVE) {
    return {
      ...baseSyncConfig,
      shared_folder_link: '',
      folder_id: '',
      shared_drive_link: '',
      shared_drive_id: '',
    };
  }

  if (connectorType === ConnectorType.GOOGLE_SHEETS) {
    return {
      ...baseSyncConfig,
      spreadsheet_link: '',
      spreadsheet_id: '',
      ranges: '',
      max_rows: 100,
    };
  }

  if (connectorType === ConnectorType.GMAIL) {
    return {
      ...baseSyncConfig,
      query: 'newer_than:30d',
      label_ids: '',
      max_messages: 50,
    };
  }

  if (connectorType === ConnectorType.SLACK) {
    return {
      ...baseSyncConfig,
      channel_ids: [],
      lookback_days: 30,
      max_records: 500,
      notification_recipient_email: '',
      notification_recipient_emails: [],
    };
  }

  if (connectorType === ConnectorType.OPERATING) {
    return {
      ...baseSyncConfig,
      connection_purpose: 'contract_automation',
      scope_type: 'applet',
      scope_target: 'contract_automation',
      base_url: '',
      project_status_name: 'Active',
      allocation_status_name: 'Planned',
      createPhase: true,
      phase_name: 'SOW Delivery',
      phase_color: 'blue',
      default_daily_hours: 8,
      createRoleSlots: false,
      role_map: {},
    };
  }

  return baseSyncConfig;
};

const buildInitialFormData = (connectorType: string): ConnectionFormData => ({
  connector_name: connectorType === ConnectorType.GRANOLA ? 'Granola' : '',
  connector_type: connectorType,
  api_key: '',
  erp_api_secret: '',
  erp_base_url: '',
  erp_company_id: '',
  erp_mariadb_host: '',
  erp_mariadb_port: '3306',
  erp_mariadb_database: '',
  erp_mariadb_user: '',
  erp_mariadb_password: '',
  erp_selected_tables_json: '',
  access_key_id: '',
  secret_access_key: '',
  redshift_host: '',
  redshift_port: '5439',
  redshift_database: 'dev',
  redshift_db_user: '',
  redshift_region: 'us-east-1',
  redshift_schema: 'cashflow',
  redshift_is_serverless: false,
  redshift_workgroup: '',
  snowflake_account: '',
  snowflake_username: '',
  snowflake_password: '',
  snowflake_database: '',
  snowflake_warehouse: '',
  snowflake_role: 'PUBLIC',
  snowflake_schema: 'PUBLIC',
  postgres_host: '',
  postgres_port: '5432',
  postgres_database: '',
  postgres_username: '',
  postgres_password: '',
  postgres_schema: 'public',
  postgres_sslmode: 'require',
  postgres_secret_id: '',
  postgres_region: 'us-east-1',
  mariadb_host: '',
  mariadb_port: '3306',
  mariadb_database: '',
  mariadb_username: '',
  mariadb_password: '',
  mariadb_include_table_patterns: '',
  mariadb_exclude_table_patterns: '',
  bigquery_project_id: '',
  bigquery_client_id: '',
  bigquery_client_secret: '',
  bigquery_redirect_uri: '',
  search_query: {},
  sync_config: buildInitialSyncConfig(connectorType),
  sync_mode: SyncMode.FULL_REFRESH,
  sync_schedule: undefined,
  description: '',
});

export function CreateConnectionModal({ isOpen, onClose, defaultConnectorType }: CreateConnectionModalProps) {
  const queryClient = useQueryClient();
  const { push: addToast } = useToasts();

  const resolvedConnectorType = defaultConnectorType ?? ConnectorType.PEOPLE_DATA_LABS;

  // Form state
  const [formData, setFormData] = useState<ConnectionFormData>(
    buildInitialFormData(resolvedConnectorType),
  );
  const [slackRecipientsInput, setSlackRecipientsInput] = useState('');

  const [queryJson, setQueryJson] = useState('{}');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [costEstimate, setCostEstimate] = useState<any>(null);
  const [isStartingOauth, setIsStartingOauth] = useState(false);
  const [bigQueryCallbackUri, setBigQueryCallbackUri] = useState('');
  const [bigQueryCallbackError, setBigQueryCallbackError] = useState<string | null>(null);
  const [isLoadingBigQueryCallback, setIsLoadingBigQueryCallback] = useState(false);

  // Mutations
  const { mutate: createConnector, isPending: isCreating } = useCreateConnector({
    mutation: {
      onSuccess: () => {
        addToast({
          kind: 'success',
          message:
            formData.connector_type === ConnectorType.BIGQUERY
              ? 'BigQuery connection saved. Select it and click Authorize Google next.'
              : 'Connection created successfully!',
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
        handleClose();
      },
      onError: (error: any) => {
        addToast({
          kind: 'error',
          message: error?.response?.data?.detail || 'Failed to create connection.',
        });
      },
    },
  });

  const { mutate: testConnection, isPending: isTesting } = useTestConnection({
    mutation: {
      onSuccess: (data) => {
        setTestResult({ success: data.success, message: data.message });
        addToast({
          kind: data.success ? 'success' : 'error',
          message: data.message,
        });
      },
      onError: (error: any) => {
        const message = error?.response?.data?.detail || error?.message || 'Connection test failed.';
        setTestResult({ success: false, message });
        addToast({
          kind: 'error',
          message,
        });
      },
    },
  });

  const { mutate: estimateCost, isPending: isEstimating } = useEstimateCost({
    mutation: {
      onSuccess: (data: any) => {
        setCostEstimate(data);
        addToast({
          kind: 'info',
          message: `Estimated ${data.estimated_record_count} records (~$${data.estimated_cost.toFixed(2)})`,
        });
      },
      onError: (error: any) => {
        addToast({
          kind: 'error',
          message: error?.response?.data?.detail || 'Cost estimation failed.',
        });
      },
    },
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialFormData(resolvedConnectorType));
      setSlackRecipientsInput('');
    }
  }, [isOpen, resolvedConnectorType]);

  useEffect(() => {
    if (!isOpen || formData.connector_type !== ConnectorType.BIGQUERY) {
      return;
    }

    let cancelled = false;
    setIsLoadingBigQueryCallback(true);
    setBigQueryCallbackError(null);

    AXIOS_INSTANCE.get<{ callback_uri: string }>('/api/connectors/bigquery/oauth/callback-uri')
      .then(({ data }) => {
        if (cancelled) return;
        const callbackUri = data.callback_uri || '';
        setBigQueryCallbackUri(callbackUri);
        setFormData((current) => {
          if (current.connector_type !== ConnectorType.BIGQUERY || current.bigquery_redirect_uri) {
            return current;
          }
          return { ...current, bigquery_redirect_uri: callbackUri };
        });
      })
      .catch((error: any) => {
        if (cancelled) return;
        setBigQueryCallbackError(
          error?.response?.data?.detail || error?.message || 'Unable to load BigQuery callback URI.',
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingBigQueryCallback(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.connector_type]);

  const handleClose = () => {
    setFormData(buildInitialFormData(resolvedConnectorType));
    setQueryJson('{}');
    setTestResult(null);
    setCostEstimate(null);
    setIsStartingOauth(false);
    setBigQueryCallbackUri('');
    setBigQueryCallbackError(null);
    setIsLoadingBigQueryCallback(false);
    onClose();
  };

  const handleGranolaOAuth = async () => {
    if (isStartingOauth) return;
    setIsStartingOauth(true);
    try {
      const { data } = await AXIOS_INSTANCE.get('/oauth/authorize/granola');
      const popup = window.open(data.authorize_url, 'granola_oauth', 'width=640,height=760');
      if (!popup) {
        throw new Error('Popup blocked');
      }

      const cleanup = () => {
        window.removeEventListener('message', handler);
        clearInterval(interval);
        setIsStartingOauth(false);
      };

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth_complete' && event.data?.source === 'granola') {
          cleanup();
          addToast({ kind: 'success', message: 'Granola connected successfully!' });
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
          handleClose();
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (popup?.closed) {
          cleanup();
        }
      }, 1000);
    } catch (error: any) {
      setIsStartingOauth(false);
      addToast({
        kind: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to initiate Granola OAuth.',
      });
    }
  };

  const handleGoogleOAuth = async () => {
    if (isStartingOauth) return;
    if (!formData.connector_name || !hasGoogleTargetConfig(formData)) {
      addToast({ kind: 'error', message: 'Add a connection name and the required Google source details.' });
      return;
    }

    const popup = window.open('about:blank', 'google_workspace_oauth', 'width=640,height=760');
    if (!popup) {
      addToast({ kind: 'error', message: 'Popup blocked. Allow popups and try again.' });
      return;
    }

    setIsStartingOauth(true);
    popup.document.write('<p style="font-family: sans-serif; padding: 16px;">Preparing Google OAuth...</p>');
    let oauthCompleted = false;

    try {
      const folderLink = String(formData.sync_config?.shared_folder_link || '').trim();
      const folderId = String(formData.sync_config?.folder_id || '').trim();
      const sharedDriveLink = String(formData.sync_config?.shared_drive_link || '').trim();
      const sharedDriveId = String(formData.sync_config?.shared_drive_id || '').trim();
      const spreadsheetLink = String(formData.sync_config?.spreadsheet_link || '').trim();
      const spreadsheetId = String(formData.sync_config?.spreadsheet_id || '').trim();
      const syncConfig = {
        ...(formData.sync_config || {}),
        ...(folderLink ? { shared_folder_link: folderLink } : {}),
        ...(folderId ? { folder_id: folderId } : {}),
        ...(sharedDriveLink ? { shared_drive_link: sharedDriveLink } : {}),
        ...(sharedDriveId ? { shared_drive_id: sharedDriveId } : {}),
        ...(spreadsheetLink ? { spreadsheet_link: spreadsheetLink } : {}),
        ...(spreadsheetId ? { spreadsheet_id: spreadsheetId } : {}),
      };
      const connector = await createConnectorConfigurationApiConnectorsConfigurationsPost({
        connector_name: formData.connector_name,
        connector_type: formData.connector_type,
        credentials: { auth_method: 'oauth' },
        sync_config: syncConfig,
        sync_mode: formData.sync_mode,
        description: formData.description,
        sync_schedule: formData.sync_schedule,
      });
      const authorization = await googleWorkspaceAuthorizeOauthAuthorizeGoogleGet({
        connector_config_id: connector.id,
        return_origin: window.location.origin,
      });
      popup.location.href = authorization.authorize_url;

      const cleanup = () => {
        window.removeEventListener('message', handler);
        window.clearInterval(interval);
        setIsStartingOauth(false);
      };
      const refreshConnectors = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
      };
      const handler = (event: MessageEvent) => {
        if (
          event.source === popup &&
          event.data?.type === 'oauth_complete' &&
          event.data?.source === formData.connector_type
        ) {
          oauthCompleted = true;
          cleanup();
          refreshConnectors();
          addToast({ kind: 'success', message: 'Google connection authorized.' });
          handleClose();
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (!popup.closed || oauthCompleted) return;
        cleanup();
        refreshConnectors();
        handleClose();
      }, 1000);
    } catch (error: any) {
      if (!popup.closed) popup.close();
      setIsStartingOauth(false);
      addToast({
        kind: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to initiate Google OAuth.',
      });
    }
  };

  const handleTestConnection = () => {
    let credentials: Record<string, any>;
    let syncConfig = formData.sync_config;

    if (formData.connector_type === ConnectorType.S3) {
      credentials = {
        access_key_id: formData.access_key_id,
        secret_access_key: formData.secret_access_key,
      };
    } else if (formData.connector_type === ConnectorType.REDSHIFT) {
      // Serverless auto-detects from the host endpoint but the user can
      // also force it on. db_user is only sent for cluster-mode IAM.
      const serverless =
        formData.redshift_is_serverless || isServerlessHost(formData.redshift_host);
      credentials = {
        host: formData.redshift_host,
        port: formData.redshift_port,
        database: formData.redshift_database,
        access_key_id: formData.access_key_id,
        secret_access_key: formData.secret_access_key,
        region: formData.redshift_region,
      };
      if (!serverless && formData.redshift_db_user) {
        credentials.db_user = formData.redshift_db_user;
      }
      syncConfig = {
        ...syncConfig,
        schema: formData.redshift_schema,
        is_serverless: serverless,
        ...(formData.redshift_workgroup
          ? { serverless_work_group: formData.redshift_workgroup }
          : {}),
      };
    } else if (formData.connector_type === ConnectorType.BIGQUERY) {
      // Handled by handleBigQueryOAuth early return
      return;
    } else if (formData.connector_type === ConnectorType.SNOWFLAKE) {
      credentials = {
        account: formData.snowflake_account,
        username: formData.snowflake_username,
        password: formData.snowflake_password,
        database: formData.snowflake_database,
        warehouse: formData.snowflake_warehouse,
        role: formData.snowflake_role,
      };
      syncConfig = {
        ...formData.sync_config,
        schema: formData.snowflake_schema,
      };
    } else if (formData.connector_type === ConnectorType.ERP) {
      const parsedTables = parseERPSelectedTablesJson(formData.erp_selected_tables_json);
      if (parsedTables.error) {
        addToast({ kind: 'error', message: parsedTables.error });
        return;
      }
      const erpSourceMode = getERPAnalyticsSourceMode(formData);
      if (
        erpSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB &&
        missingERPAnalyticsCredential(formData)
      ) {
        addToast({ kind: 'error', message: 'MariaDB credentials are required for advanced ERP analytics table sync.' });
        return;
      }
      credentials = {
        api_key: formData.api_key,
        api_secret: formData.erp_api_secret,
        ...(erpSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB
          ? buildERPAnalyticsCredentials(formData)
          : {}),
      };
      syncConfig = buildERPSyncConfig(formData, parsedTables.selectedTables);
    } else if (formData.connector_type === ConnectorType.POSTGRES) {
      const secretId = formData.postgres_secret_id.trim();
      credentials = secretId
        ? {
            secret_id: secretId,
            region: formData.postgres_region,
          }
        : {
            host: formData.postgres_host,
            port: formData.postgres_port,
            database: formData.postgres_database,
            username: formData.postgres_username,
            password: formData.postgres_password,
            sslmode: formData.postgres_sslmode,
          };
      syncConfig = {
        ...formData.sync_config,
        schema: formData.postgres_schema,
        sslmode: formData.postgres_sslmode,
        ...(secretId ? { secret_id: secretId, region: formData.postgres_region } : {}),
      };
    } else if (formData.connector_type === ConnectorType.MARIADB) {
      credentials = {
        mariadb_host: formData.mariadb_host.trim(),
        mariadb_port: formData.mariadb_port.trim() || '3306',
        mariadb_database: formData.mariadb_database.trim(),
        mariadb_user: formData.mariadb_username.trim(),
        mariadb_password: formData.mariadb_password,
      };
      syncConfig = buildMariaDBSyncConfig(formData);
    } else if (formData.connector_type === ConnectorType.SLACK) {
      credentials = { bot_token: formData.api_key };
      syncConfig = formData.sync_config || {};
    } else if (formData.connector_type === ConnectorType.OPERATING) {
      credentials = { api_key: formData.api_key };
      syncConfig = formData.sync_config || {};
    } else {
      credentials = { api_key: formData.api_key };
      syncConfig = undefined;
    }

    testConnection({
      data: {
        connector_type: formData.connector_type,
        credentials,
        sync_config: syncConfig,
      },
    });
  };

  const handleEstimateCost = () => {
    try {
      const query = JSON.parse(queryJson);
      estimateCost({
        data: {
          connector_type: formData.connector_type,
          credentials: { api_key: formData.api_key },
          sync_config: query,
        },
      });
    } catch {
      addToast({ kind: 'error', message: 'Invalid JSON in search query' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isAppletScoped =
      formData.sync_config?.connection_purpose !== 'tenant_object_storage' &&
      formData.sync_config?.scope_type === 'applet';

    if (formData.connector_type === ConnectorType.GRANOLA) {
      void handleGranolaOAuth();
      return;
    }

    if (isGoogleOAuthConnector(formData.connector_type)) {
      void handleGoogleOAuth();
      return;
    }

    if (formData.connector_type === ConnectorType.BIGQUERY) {
      const redirectUri = formData.bigquery_redirect_uri || bigQueryCallbackUri;
      if (
        !formData.connector_name ||
        !formData.bigquery_project_id ||
        !formData.bigquery_client_id ||
        !formData.bigquery_client_secret ||
        !redirectUri
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required BigQuery OAuth fields.' });
        return;
      }
      if (isAppletScoped && !String(formData.sync_config?.scope_target || '').trim()) {
        addToast({ kind: 'error', message: 'Applet key is required for applet-scoped connections.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            auth_method: 'oauth',
            project_id: formData.bigquery_project_id,
            client_id: formData.bigquery_client_id,
            client_secret: formData.bigquery_client_secret,
            redirect_uri: redirectUri,
          },
          sync_config: formData.sync_config || {},
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
      return;
    }

    if (isAppletScoped && !String(formData.sync_config?.scope_target || '').trim()) {
      addToast({ kind: 'error', message: 'Applet key is required for applet-scoped connections.' });
      return;
    }

    // Validate based on connector type
    if (formData.connector_type === ConnectorType.S3) {
      if (
        !formData.connector_name ||
        !formData.access_key_id ||
        !formData.secret_access_key ||
        !formData.sync_config?.bucket
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }

      if (
        formData.sync_config?.backend === 'minio' &&
        !String(formData.sync_config?.endpoint_url || '').trim()
      ) {
        addToast({ kind: 'error', message: 'Endpoint URL is required for MinIO connections.' });
        return;
      }

      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            access_key_id: formData.access_key_id,
            secret_access_key: formData.secret_access_key,
          },
          sync_config: formData.sync_config || {},
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (formData.connector_type === ConnectorType.REDSHIFT) {
      const serverless =
        formData.redshift_is_serverless || isServerlessHost(formData.redshift_host);
      // Classic clusters need a db_user for IAM auth; serverless does not.
      if (
        !formData.connector_name ||
        !formData.redshift_host ||
        !formData.access_key_id ||
        !formData.secret_access_key ||
        (!serverless && !formData.redshift_db_user)
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }

      const credentials: Record<string, string> = {
        host: formData.redshift_host,
        port: formData.redshift_port,
        database: formData.redshift_database,
        access_key_id: formData.access_key_id,
        secret_access_key: formData.secret_access_key,
        region: formData.redshift_region,
      };
      if (!serverless && formData.redshift_db_user) {
        credentials.db_user = formData.redshift_db_user;
      }

      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials,
          sync_config: {
            ...formData.sync_config,
            schema: formData.redshift_schema,
            is_serverless: serverless,
            ...(formData.redshift_workgroup
              ? { serverless_work_group: formData.redshift_workgroup }
              : {}),
          },
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (formData.connector_type === ConnectorType.FILESYSTEM) {
      if (!formData.connector_name || !formData.sync_config?.directory_path) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {},
          sync_config: formData.sync_config || {},
          sync_mode: formData.sync_mode,
          description: formData.description,
          sync_schedule: formData.sync_schedule,
        },
      });
    } else if (formData.connector_type === ConnectorType.SLACK) {
      if (!formData.connector_name || !formData.api_key) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: { bot_token: formData.api_key },
          sync_config: formData.sync_config || {},
          sync_mode: formData.sync_mode,
          description: formData.description,
          sync_schedule: formData.sync_schedule,
        },
      });
    } else if (formData.connector_type === ConnectorType.GREENHOUSE) {
      if (!formData.connector_name || !formData.api_key) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: { api_key: formData.api_key },
          sync_config: {
            ...formData.sync_config,
            max_candidates: formData.sync_config?.max_candidates || 50,
            job_ids: formData.sync_config?.job_ids || '',
          },
          sync_mode: formData.sync_mode,
          description: formData.description,
          sync_schedule: formData.sync_schedule,
        },
      });
    } else if (formData.connector_type === ConnectorType.OPERATING) {
      if (
        !formData.connector_name ||
        !formData.api_key ||
        !String(formData.sync_config?.base_url || '').trim()
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: { api_key: formData.api_key },
          sync_config: formData.sync_config || {},
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (
      formData.connector_type === ConnectorType.HUBSPOT ||
      formData.connector_type === ConnectorType.FATHOM
    ) {
      if (!formData.connector_name || !formData.api_key) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: { api_key: formData.api_key },
          sync_config: formData.sync_config || {},
          sync_mode: formData.sync_mode,
          description: formData.description,
          sync_schedule: formData.sync_schedule,
        },
      });

    } else if (formData.connector_type === ConnectorType.ATLAN) {
      if (!formData.connector_name || !formData.redshift_host || !formData.api_key) {
        addToast({ kind: 'error', message: 'Please fill in all required fields. For Atlan, Host goes into the Host field.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            host: formData.redshift_host,
            api_key: formData.api_key,
          },
          sync_config: formData.sync_config || {},
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (formData.connector_type === ConnectorType.SNOWFLAKE) {
      if (
        !formData.connector_name ||
        !formData.snowflake_account ||
        !formData.snowflake_username ||
        !formData.snowflake_password ||
        !formData.snowflake_database
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            account: formData.snowflake_account,
            username: formData.snowflake_username,
            password: formData.snowflake_password,
            database: formData.snowflake_database,
            warehouse: formData.snowflake_warehouse,
            role: formData.snowflake_role,
          },
          sync_config: {
            ...formData.sync_config,
            schema: formData.snowflake_schema,
          },
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (formData.connector_type === ConnectorType.ERP) {
      if (
        !formData.connector_name ||
        !formData.api_key ||
        !formData.erp_api_secret ||
        !formData.erp_base_url
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required ERP fields.' });
        return;
      }
      const parsedTables = parseERPSelectedTablesJson(formData.erp_selected_tables_json);
      if (parsedTables.error) {
        addToast({ kind: 'error', message: parsedTables.error });
        return;
      }
      const erpSourceMode = getERPAnalyticsSourceMode(formData);
      if (
        erpSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB &&
        missingERPAnalyticsCredential(formData)
      ) {
        addToast({ kind: 'error', message: 'MariaDB credentials are required for advanced ERP analytics table sync.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            api_key: formData.api_key,
            api_secret: formData.erp_api_secret,
            ...(erpSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB
              ? buildERPAnalyticsCredentials(formData)
              : {}),
          },
          sync_config: buildERPSyncConfig(formData, parsedTables.selectedTables),
          sync_mode: formData.sync_mode,
          description: formData.description,
          sync_schedule: formData.sync_schedule,
        },
      });
    } else if (formData.connector_type === ConnectorType.POSTGRES) {
      const secretId = formData.postgres_secret_id.trim();
      const hasDirectCredentials =
        formData.postgres_host &&
        formData.postgres_database &&
        formData.postgres_username &&
        formData.postgres_password;
      if (!formData.connector_name || (!secretId && !hasDirectCredentials)) {
        addToast({
          kind: 'error',
          message:
            'Please provide a connection name and either a Secrets Manager secret id or direct Postgres credentials.',
        });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: secretId
            ? {
                secret_id: secretId,
                region: formData.postgres_region,
              }
            : {
                host: formData.postgres_host,
                port: formData.postgres_port,
                database: formData.postgres_database,
                username: formData.postgres_username,
                password: formData.postgres_password,
                sslmode: formData.postgres_sslmode,
              },
          sync_config: {
            ...formData.sync_config,
            schema: formData.postgres_schema,
            sslmode: formData.postgres_sslmode,
            ...(secretId ? { secret_id: secretId, region: formData.postgres_region } : {}),
          },
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else if (formData.connector_type === ConnectorType.MARIADB) {
      if (
        !formData.connector_name ||
        !formData.mariadb_host ||
        !formData.mariadb_database ||
        !formData.mariadb_username ||
        !formData.mariadb_password
      ) {
        addToast({ kind: 'error', message: 'Please fill in all required MariaDB fields.' });
        return;
      }
      createConnector({
        data: {
          connector_name: formData.connector_name,
          connector_type: formData.connector_type,
          credentials: {
            mariadb_host: formData.mariadb_host.trim(),
            mariadb_port: formData.mariadb_port.trim() || '3306',
            mariadb_database: formData.mariadb_database.trim(),
            mariadb_user: formData.mariadb_username.trim(),
            mariadb_password: formData.mariadb_password,
          },
          sync_config: buildMariaDBSyncConfig(formData),
          sync_mode: SyncMode.FULL_REFRESH,
          description: formData.description,
        },
      });
    } else {
      if (!formData.connector_name || !formData.api_key) {
        addToast({ kind: 'error', message: 'Please fill in all required fields.' });
        return;
      }
      try {
        const query = JSON.parse(queryJson);
        createConnector({
          data: {
            connector_name: formData.connector_name,
            connector_type: formData.connector_type,
            credentials: { api_key: formData.api_key },
            sync_config: { ...formData.sync_config, search_query: query },
            sync_mode: formData.sync_mode,
            description: formData.description,
            sync_schedule: formData.sync_schedule,
          },
        });
      } catch {
        addToast({ kind: 'error', message: 'Invalid JSON in search query' });
      }
    }
  };

  const applyQueryTemplate = (templateKey: keyof typeof PDL_QUERY_TEMPLATES) => {
    const template = PDL_QUERY_TEMPLATES[templateKey];
    setQueryJson(JSON.stringify(template, null, 2));
    setFormData({ ...formData, search_query: template });
  };

  const connectorOptions = CONNECTOR_CARD_ORDER.map((connectorType) => ({
    value: connectorType,
    ...getConnectorCatalogEntry(connectorType),
    available: true,
  }));
  const selectedConnector = getConnectorCatalogEntry(formData.connector_type);
  const isGranola = formData.connector_type === ConnectorType.GRANOLA;
  const hasGoogleSourceDetails = hasGoogleTargetConfig(formData);
  const isERP = formData.connector_type === ConnectorType.ERP;
  const erpAnalyticsSourceMode = getERPAnalyticsSourceMode(formData);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-surface border border-border shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10">
                      <LinkIcon className="h-5 w-5 text-brand" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-text">
                        New Source Connection
                      </Dialog.Title>
                      <p className="text-xs text-muted">Choose a source type, then save a reusable connection instance</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-surface-2 rounded-lg transition-colors text-muted hover:text-text"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-220px)] overflow-y-auto">
                    {/* Connector Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-text mb-2">
                        Source Type
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {connectorOptions.map((option) => {
                          const Icon = option.icon;
                          const isSelected = formData.connector_type === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                const nextForm = buildInitialFormData(option.value);
                                setFormData({
                                  ...nextForm,
                                  connector_name:
                                    option.value === ConnectorType.GRANOLA
                                      ? nextForm.connector_name
                                      : formData.connector_name,
                                  description: formData.description,
                                });
                                setQueryJson('{}');
                                setTestResult(null);
                                setCostEstimate(null);
                              }}
                              disabled={!option.available}
                              className={`
                                p-4 rounded-xl border-2 text-left transition-all duration-200
                                ${isSelected
                                  ? 'border-brand bg-brand/5'
                                  : 'border-border hover:border-brand/50 hover:bg-surface-2'
                                }
                                ${!option.available && 'opacity-50 cursor-not-allowed'}
                              `}
                            >
                              <Icon className={`h-6 w-6 mb-2 ${isSelected ? 'text-brand' : 'text-muted'}`} />
                              <p className={`text-sm font-medium ${isSelected ? 'text-brand' : 'text-text'}`}>
                                {option.label}
                              </p>
                              <p className="text-xs text-muted mt-0.5">{option.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Connection Name */}
                    <div>
                      <label className="block text-sm font-medium text-text mb-2">
                        Connection Name <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.connector_name}
                        onChange={(e) => setFormData({ ...formData, connector_name: e.target.value })}
                        placeholder={`e.g., ${selectedConnector?.label || 'My'} Production Connection`}
                        className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                        disabled={isGranola}
                        required
                      />
                      <p className="mt-1.5 text-xs text-muted">
                        This is the saved connection instance. The source type stays the same; the name identifies this specific configured connection.
                      </p>
                    </div>

                    {/* S3-specific fields */}
                    {formData.connector_type === ConnectorType.S3 && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Backend
                            </label>
                            <select
                              value={formData.sync_config?.backend || 's3'}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    backend: e.target.value,
                                    force_path_style:
                                      e.target.value === 'minio'
                                        ? true
                                        : formData.sync_config?.force_path_style || false,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            >
                              <option value="s3">AWS S3</option>
                              <option value="minio">MinIO</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Bucket <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.bucket || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: { ...formData.sync_config, bucket: e.target.value },
                                })
                              }
                              placeholder="tenant-documents"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Region
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.region || 'us-east-1'}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: { ...formData.sync_config, region: e.target.value },
                                })
                              }
                              placeholder="us-east-1"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Endpoint URL
                              <span className="text-xs text-muted">
                                {formData.sync_config?.backend === 'minio' ? ' (required for MinIO)' : ' (optional)'}
                              </span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.endpoint_url || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    endpoint_url: e.target.value,
                                  },
                                })
                              }
                              placeholder="https://minio.company.net"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Path Prefix
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.path_prefix || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    path_prefix: e.target.value,
                                  },
                                })
                              }
                              placeholder="rag/workspaces"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Default Local Source Path
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.default_local_source_path || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    default_local_source_path: e.target.value,
                                  },
                                })
                              }
                              placeholder="/imports/hr/"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>

                        {formData.sync_config?.backend !== 'minio' && (
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={Boolean(formData.sync_config?.force_path_style)}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    force_path_style: e.target.checked,
                                  },
                                })
                              }
                              className="h-4 w-4 rounded border-border text-brand focus:ring-2 focus:ring-brand/50"
                            />
                            <span className="text-sm text-text">Force path-style URLs</span>
                          </label>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Credentials <span className="text-error">*</span>
                          </label>
                          <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                            <input
                              type="text"
                              value={formData.access_key_id}
                              onChange={(e) => setFormData({ ...formData, access_key_id: e.target.value })}
                              placeholder="Access key ID"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <input
                              type="password"
                              value={formData.secret_access_key}
                              onChange={(e) => setFormData({ ...formData, secret_access_key: e.target.value })}
                              placeholder="Secret access key"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={
                                !formData.access_key_id ||
                                !formData.secret_access_key ||
                                !formData.sync_config?.bucket ||
                                isTesting
                              }
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          {testResult && (
                            <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}>
                              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                              <span>{testResult.message}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Redshift-specific fields */}
                    {formData.connector_type === ConnectorType.REDSHIFT && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">IAM Authentication</p>
                          <p className="mt-1 text-muted">
                            Connect using AWS IAM credentials. Serverless workgroups auto-detect
                            from the host; classic clusters additionally need a database user.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Host <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.redshift_host}
                              onChange={(e) => {
                                const host = e.target.value;
                                setFormData((previous) => ({
                                  ...previous,
                                  redshift_host: host,
                                  // Auto-flip serverless when the host looks
                                  // like a serverless endpoint; still lets the
                                  // user override with the checkbox below.
                                  redshift_is_serverless:
                                    previous.redshift_is_serverless || isServerlessHost(host),
                                }));
                              }}
                              placeholder="workgroup.123456789.us-east-1.redshift-serverless.amazonaws.com"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Port
                            </label>
                            <input
                              type="text"
                              value={formData.redshift_port}
                              onChange={(e) => setFormData({ ...formData, redshift_port: e.target.value })}
                              placeholder="5439"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Database <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.redshift_database}
                              onChange={(e) => setFormData({ ...formData, redshift_database: e.target.value })}
                              placeholder="dev"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Schema
                            </label>
                            <input
                              type="text"
                              value={formData.redshift_schema}
                              onChange={(e) => setFormData({ ...formData, redshift_schema: e.target.value })}
                              placeholder="cashflow"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Region
                            </label>
                            <input
                              type="text"
                              value={formData.redshift_region}
                              onChange={(e) => setFormData({ ...formData, redshift_region: e.target.value })}
                              placeholder="us-east-1"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-surface-2 px-4 py-3 space-y-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.redshift_is_serverless}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  redshift_is_serverless: e.target.checked,
                                })
                              }
                              className="h-4 w-4 rounded border-border text-brand focus:ring-brand/50"
                            />
                            <span className="text-sm font-medium text-text">
                              Redshift Serverless
                            </span>
                            <span className="text-xs text-muted">
                              Auto-detected from the host endpoint.
                            </span>
                          </label>

                          {formData.redshift_is_serverless ? (
                            <div>
                              <label className="block text-sm font-medium text-text mb-2">
                                Workgroup <span className="text-muted">(optional)</span>
                              </label>
                              <input
                                type="text"
                                value={formData.redshift_workgroup}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    redshift_workgroup: e.target.value,
                                  })
                                }
                                placeholder="my-workgroup"
                                className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              />
                              <p className="mt-1.5 text-xs text-muted">
                                Only needed when the host isn't a workgroup endpoint. The driver
                                routes to the right serverless endpoint automatically when the host
                                ends in <code>.redshift-serverless.amazonaws.com</code>.
                              </p>
                            </div>
                          ) : (
                            <div>
                              <label className="block text-sm font-medium text-text mb-2">
                                Database User <span className="text-error">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.redshift_db_user}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    redshift_db_user: e.target.value,
                                  })
                                }
                                placeholder="admin"
                                className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                required={!formData.redshift_is_serverless}
                              />
                              <p className="mt-1.5 text-xs text-muted">
                                The Redshift user to authenticate as via IAM temporary credentials
                                (classic clusters only).
                              </p>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            AWS Credentials <span className="text-error">*</span>
                          </label>
                          <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                            <input
                              type="text"
                              value={formData.access_key_id}
                              onChange={(e) => setFormData({ ...formData, access_key_id: e.target.value })}
                              placeholder="Access Key ID"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <input
                              type="password"
                              value={formData.secret_access_key}
                              onChange={(e) => setFormData({ ...formData, secret_access_key: e.target.value })}
                              placeholder="Secret Access Key"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={
                                !formData.redshift_host ||
                                (!formData.redshift_is_serverless &&
                                  !isServerlessHost(formData.redshift_host) &&
                                  !formData.redshift_db_user) ||
                                !formData.access_key_id ||
                                !formData.secret_access_key ||
                                isTesting
                              }
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          {testResult && (
                            <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}>
                              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                              <span>{testResult.message}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {formData.connector_type === ConnectorType.BIGQUERY && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">Google Cloud Authentication</p>
                          <p className="mt-1 text-muted">
                            Save this tenant's Google OAuth app details, then authorize BigQuery access with Google.
                            Register the callback URI below in the Google Cloud OAuth client.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Google Cloud Project ID *
                            </label>
                            <input
                              type="text"
                              value={formData.bigquery_project_id}
                              onChange={(e) => setFormData({ ...formData, bigquery_project_id: e.target.value })}
                              placeholder="e.g., venetian-production"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              OAuth Client ID *
                            </label>
                            <input
                              type="text"
                              value={formData.bigquery_client_id}
                              onChange={(e) => setFormData({ ...formData, bigquery_client_id: e.target.value })}
                              placeholder="Client ID from Google Cloud"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              OAuth Client Secret *
                            </label>
                            <input
                              type="password"
                              value={formData.bigquery_client_secret}
                              onChange={(e) => setFormData({ ...formData, bigquery_client_secret: e.target.value })}
                              placeholder="Client secret from Google Cloud"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Redirect URI *
                            </label>
                            <input
                              type="text"
                              value={formData.bigquery_redirect_uri}
                              onChange={(e) => setFormData({ ...formData, bigquery_redirect_uri: e.target.value })}
                              placeholder={isLoadingBigQueryCallback ? 'Loading callback URI...' : 'OAuth callback URI'}
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-surface-2 px-4 py-3 text-sm">
                          <div className="flex items-start gap-2">
                            <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted" />
                            <div className="min-w-0">
                              <p className="font-medium text-text">Google OAuth callback URI</p>
                              <p className="mt-1 break-all font-mono text-xs text-muted">
                                {bigQueryCallbackUri || formData.bigquery_redirect_uri || 'Callback URI unavailable'}
                              </p>
                              {bigQueryCallbackError && (
                                <p className="mt-2 text-xs text-error">{bigQueryCallbackError}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {testResult && (
                          <div className={`mt-4 p-3 rounded-xl flex items-start gap-2 text-sm ${
                            testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Snowflake-specific fields */}
                    {formData.connector_type === ConnectorType.SNOWFLAKE && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">Snowflake Authentication</p>
                          <p className="mt-1 text-muted">
                            Connect using your Snowflake account credentials. Ensure the user has
                            appropriate warehouse and database usage permissions.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                              Account URL / Locator <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_account}
                              onChange={(e) => setFormData({ ...formData, snowflake_account: e.target.value })}
                              placeholder="xy12345.us-east-1"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Username <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_username}
                              onChange={(e) => setFormData({ ...formData, snowflake_username: e.target.value })}
                              placeholder="ELIZA_USER"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Password <span className="text-error">*</span>
                            </label>
                            <input
                              type="password"
                              value={formData.snowflake_password}
                              onChange={(e) => setFormData({ ...formData, snowflake_password: e.target.value })}
                              placeholder="••••••••"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Database
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_database}
                              onChange={(e) => setFormData({ ...formData, snowflake_database: e.target.value })}
                              placeholder="PROD_DB"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Warehouse
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_warehouse}
                              onChange={(e) => setFormData({ ...formData, snowflake_warehouse: e.target.value })}
                              placeholder="COMPUTE_WH"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Role
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_role}
                              onChange={(e) => setFormData({ ...formData, snowflake_role: e.target.value })}
                              placeholder="PUBLIC"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Schema
                            </label>
                            <input
                              type="text"
                              value={formData.snowflake_schema}
                              onChange={(e) => setFormData({ ...formData, snowflake_schema: e.target.value })}
                              placeholder="PUBLIC"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={
                              !formData.snowflake_account ||
                              !formData.snowflake_username ||
                              !formData.snowflake_password ||
                              !formData.snowflake_database ||
                              isTesting
                            }
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                            Test Connection
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                            testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* PostgreSQL-specific fields */}
                    {formData.connector_type === ConnectorType.POSTGRES && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">PostgreSQL Authentication</p>
                          <p className="mt-1 text-muted">
                            Connect with an AWS Secrets Manager secret id/ARN or direct Postgres credentials. Use a read-only database user for Agent Mesh queries.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                              AWS Secrets Manager Secret ID / ARN
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_secret_id}
                              onChange={(e) => setFormData({ ...formData, postgres_secret_id: e.target.value })}
                              placeholder="example/postgres/reader"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              AWS Region
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_region}
                              onChange={(e) => setFormData({ ...formData, postgres_region: e.target.value })}
                              placeholder="us-east-1"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Schema(s)
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_schema}
                              onChange={(e) => setFormData({ ...formData, postgres_schema: e.target.value })}
                              placeholder="ttc_common,trafalgar,uniworld"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div className="col-span-2 border-t border-border pt-4 text-xs font-medium uppercase tracking-wide text-muted">
                            Direct credentials
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Host
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_host}
                              onChange={(e) => setFormData({ ...formData, postgres_host: e.target.value })}
                              placeholder="db.example.us-east-1.rds.amazonaws.com"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Port
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_port}
                              onChange={(e) => setFormData({ ...formData, postgres_port: e.target.value })}
                              placeholder="5432"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Database
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_database}
                              onChange={(e) => setFormData({ ...formData, postgres_database: e.target.value })}
                              placeholder="analytics"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Username
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_username}
                              onChange={(e) => setFormData({ ...formData, postgres_username: e.target.value })}
                              placeholder="readonly_user"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Password
                            </label>
                            <input
                              type="password"
                              value={formData.postgres_password}
                              onChange={(e) => setFormData({ ...formData, postgres_password: e.target.value })}
                              placeholder="••••••••"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              SSL Mode
                            </label>
                            <input
                              type="text"
                              value={formData.postgres_sslmode}
                              onChange={(e) => setFormData({ ...formData, postgres_sslmode: e.target.value })}
                              placeholder="require"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={
                              isTesting ||
                              (!formData.postgres_secret_id.trim() &&
                                (!formData.postgres_host ||
                                  !formData.postgres_database ||
                                  !formData.postgres_username ||
                                  !formData.postgres_password))
                            }
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                            Test Connection
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                            testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* MariaDB-specific fields */}
                    {formData.connector_type === ConnectorType.MARIADB && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">MariaDB live structured source</p>
                          <p className="mt-1 text-muted">
                            Connect a read-only MariaDB user for live Agent Mesh queries. Schema metadata syncs into the warehouse registry; rows are queried live.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Host
                            </label>
                            <input
                              type="text"
                              value={formData.mariadb_host}
                              onChange={(e) => setFormData({ ...formData, mariadb_host: e.target.value })}
                              placeholder="mariadb.internal"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Port
                            </label>
                            <input
                              type="text"
                              value={formData.mariadb_port}
                              onChange={(e) => setFormData({ ...formData, mariadb_port: e.target.value })}
                              placeholder="3306"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Database
                            </label>
                            <input
                              type="text"
                              value={formData.mariadb_database}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  mariadb_database: e.target.value,
                                  sync_config: {
                                    ...formData.sync_config,
                                    database: e.target.value,
                                  },
                                })
                              }
                              placeholder="erpnext"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Username
                            </label>
                            <input
                              type="text"
                              value={formData.mariadb_username}
                              onChange={(e) => setFormData({ ...formData, mariadb_username: e.target.value })}
                              placeholder="agent_mesh_reader"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                              Password
                            </label>
                            <input
                              type="password"
                              value={formData.mariadb_password}
                              onChange={(e) => setFormData({ ...formData, mariadb_password: e.target.value })}
                              placeholder="••••••••"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div className="col-span-2 rounded-xl border border-border bg-surface-2/40 p-4">
                            <div className="flex items-start gap-3">
                              <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted" />
                              <div>
                                <p className="text-sm font-medium text-text">Source discovery</p>
                                <p className="mt-1 text-xs text-muted">
                                  Metadata sync discovers all tables in this database unless table patterns are set here.
                                </p>
                              </div>
                            </div>
                            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Include table patterns
                                </label>
                                <input
                                  type="text"
                                  value={formData.mariadb_include_table_patterns}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      mariadb_include_table_patterns: e.target.value,
                                    })
                                  }
                                  placeholder="tab%"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Exclude table patterns
                                </label>
                                <input
                                  type="text"
                                  value={formData.mariadb_exclude_table_patterns}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      mariadb_exclude_table_patterns: e.target.value,
                                    })
                                  }
                                  placeholder="tabAccess Log, tabError Log"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={
                              isTesting ||
                              !formData.mariadb_host ||
                              !formData.mariadb_database ||
                              !formData.mariadb_username ||
                              !formData.mariadb_password
                            }
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                            Test Connection
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                            testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {formData.connector_type === ConnectorType.ATLAN && (
                      <>
                        <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                          <p className="font-medium text-brand">Atlan Catalog Configuration</p>
                          <p className="mt-1 text-muted">
                            Connect your Atlan tenant to enable business context enrichment for warehouse assets.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Atlan Host <span className="text-error">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.redshift_host}
                            onChange={(e) => setFormData({ ...formData, redshift_host: e.target.value })}
                            placeholder="https://tenant.atlan.com"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            API Key <span className="text-error">*</span>
                          </label>
                          <input
                            type="password"
                            value={formData.api_key}
                            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                            placeholder="atlan_..."
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            required
                          />
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={!formData.redshift_host || !formData.api_key || isTesting}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                            Test Connection
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                            testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                          }`}>
                            {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {isGranola && (
                      <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-4 text-sm text-text">
                        <p className="font-medium text-brand">Connect with browser OAuth</p>
                        <p className="mt-1 text-muted">
                          Granola is a live AgentMesh retrieval source. You will sign in to Granola in a popup,
                          and this tenant will use that OAuth connection for meeting-note search and transcripts.
                        </p>
                      </div>
                    )}

                    {/* Filesystem-specific fields */}
                    {formData.connector_type === ConnectorType.FILESYSTEM && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Directory Path <span className="text-error">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.directory_path || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              sync_config: { ...formData.sync_config, directory_path: e.target.value },
                            })}
                            placeholder="/path/to/files"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            File Extensions
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.file_extensions?.join(', ') || ''}
                            onChange={(e) => {
                              const extensions = e.target.value.split(',').map((ext) => ext.trim()).filter((ext) => ext);
                              setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, file_extensions: extensions },
                              });
                            }}
                            placeholder=".pdf, .docx, .txt"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                          <p className="mt-1.5 text-xs text-muted">Comma-separated list of file extensions to scan</p>
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.sync_config?.recursive || false}
                            onChange={(e) => setFormData({
                              ...formData,
                              sync_config: { ...formData.sync_config, recursive: e.target.checked },
                            })}
                            className="h-4 w-4 rounded border-border text-brand focus:ring-2 focus:ring-brand/50"
                          />
                          <span className="text-sm text-text">Scan subdirectories recursively</span>
                        </label>
                      </>
                    )}

                    {/* Greenhouse specific fields */}
                    {formData.connector_type === ConnectorType.GREENHOUSE && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Harvest API Key <span className="text-error">*</span>
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={formData.api_key}
                              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                              placeholder="Enter your Greenhouse Harvest API key"
                              className="flex-1 px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={!formData.api_key || isTesting}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          <p className="mt-1.5 text-xs text-muted">
                            Get your API key from Greenhouse: Configure → Dev Center → API Credential Management
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Max Candidates
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              value={formData.sync_config?.max_candidates || 50}
                              onChange={(e) => setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, max_candidates: parseInt(e.target.value) || 50 },
                              })}
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Job IDs <span className="text-xs text-muted">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.job_ids || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, job_ids: e.target.value },
                              })}
                              placeholder="123, 456, 789"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(formData.connector_type === ConnectorType.HUBSPOT ||
                      formData.connector_type === ConnectorType.OPERATING ||
                      formData.connector_type === ConnectorType.FATHOM) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            API Key <span className="text-error">*</span>
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={formData.api_key}
                              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                              placeholder={
                                formData.connector_type === ConnectorType.HUBSPOT
                                  ? 'Enter your HubSpot private app token'
                                  : formData.connector_type === ConnectorType.OPERATING
                                    ? 'Enter your Operating API token'
                                    : 'Enter your Fathom API key'
                              }
                              className="flex-1 px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={
                                !formData.api_key ||
                                (formData.connector_type === ConnectorType.OPERATING &&
                                  !String(formData.sync_config?.base_url || '').trim()) ||
                                isTesting
                              }
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          {formData.connector_type === ConnectorType.OPERATING && (
                            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-text mb-2">
                                  API Base URL <span className="text-error">*</span>
                                </label>
                                <input
                                  type="url"
                                  value={formData.sync_config?.base_url || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: { ...formData.sync_config, base_url: e.target.value },
                                  })}
                                  placeholder="Enter API base URL"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Project Status Name
                                </label>
                                <input
                                  type="text"
                                  value={formData.sync_config?.project_status_name || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: { ...formData.sync_config, project_status_name: e.target.value },
                                  })}
                                  placeholder="Active"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Allocation Status Name
                                </label>
                                <input
                                  type="text"
                                  value={formData.sync_config?.allocation_status_name || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: { ...formData.sync_config, allocation_status_name: e.target.value },
                                  })}
                                  placeholder="Planned"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Default Daily Hours
                                </label>
                                <input
                                  type="number"
                                  min="0.1"
                                  step="0.1"
                                  value={formData.sync_config?.default_daily_hours || 8}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: {
                                      ...formData.sync_config,
                                      default_daily_hours: Number(e.target.value),
                                    },
                                  })}
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <label className="flex items-center gap-3 pt-8 text-sm font-medium text-text">
                                <input
                                  type="checkbox"
                                  checked={Boolean(formData.sync_config?.createPhase)}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: {
                                      ...formData.sync_config,
                                      createPhase: e.target.checked,
                                    },
                                  })}
                                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand/50"
                                />
                                Create SOW phase timeline
                              </label>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Phase Name
                                </label>
                                <input
                                  type="text"
                                  value={formData.sync_config?.phase_name || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: { ...formData.sync_config, phase_name: e.target.value },
                                  })}
                                  placeholder="SOW Delivery"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  Phase Color
                                </label>
                                <input
                                  type="text"
                                  value={formData.sync_config?.phase_color || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: { ...formData.sync_config, phase_color: e.target.value },
                                  })}
                                  placeholder="blue"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                              <label className="flex items-center gap-3 pt-8 text-sm font-medium text-text">
                                <input
                                  type="checkbox"
                                  checked={Boolean(formData.sync_config?.createRoleSlots)}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sync_config: {
                                      ...formData.sync_config,
                                      createRoleSlots: e.target.checked,
                                    },
                                  })}
                                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand/50"
                                />
                                Create role slots
                              </label>
                            </div>
                          )}
                          {testResult && (
                            <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}>
                              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                              <span>{testResult.message}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {formData.connector_type === ConnectorType.SLACK && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Bot Token <span className="text-error">*</span>
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={formData.api_key}
                              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                              placeholder="xoxb-..."
                              className="flex-1 px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={!formData.api_key || isTesting}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          <p className="mt-1.5 text-xs text-muted">
                            Paste a Slack Bot User OAuth token. Add channel IDs below to limit sync to specific public channels.
                          </p>
                          {testResult && (
                            <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}>
                              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                              <span>{testResult.message}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Notification Recipient Email <span className="text-xs text-muted">(optional)</span>
                          </label>
                          <input
                            type="email"
                            value={formData.sync_config?.notification_recipient_email || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              sync_config: {
                                ...formData.sync_config,
                                notification_recipient_email: e.target.value,
                              },
                            })}
                            placeholder="reviewer@company.com"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                          <p className="mt-1.5 text-xs text-muted">
                            Contract Automation sends Slack updates to this workspace email. Leave blank to use the authenticated Eliza user.
                          </p>
                        </div>

                        <div>
                          <label
                            htmlFor="slack-approved-notification-recipients"
                            className="block text-sm font-medium text-text mb-2"
                          >
                            Approved Notification Recipients <span className="text-xs text-muted">(optional, comma-separated)</span>
                          </label>
                          <input
                            id="slack-approved-notification-recipients"
                            type="text"
                            value={slackRecipientsInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSlackRecipientsInput(value);
                              setFormData({
                                ...formData,
                                sync_config: {
                                  ...formData.sync_config,
                                  notification_recipient_emails: value.split(',').map(email => email.trim()).filter(Boolean),
                                },
                              });
                            }}
                            placeholder="reviewer@company.com, approver@company.com"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                          <p className="mt-1.5 text-xs text-muted">Only these people can be selected for Contract Automation direct messages.</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Channel IDs <span className="text-xs text-muted">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={(formData.sync_config?.channel_ids || []).join(', ')}
                              onChange={(e) => {
                                const channelIds = e.target.value.split(',').map((value) => value.trim()).filter(Boolean);
                                setFormData({
                                  ...formData,
                                  sync_config: { ...formData.sync_config, channel_ids: channelIds },
                                });
                              }}
                              placeholder="C012ABCDEF, C098ZYXWV"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Messages per Channel
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="200"
                              value={formData.sync_config?.messages_per_channel || 200}
                              onChange={(e) => setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, messages_per_channel: parseInt(e.target.value, 10) || 200 },
                              })}
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {isGoogleOAuthConnector(formData.connector_type) && (
                      <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3">
                        <LinkIcon className="h-5 w-5 text-brand" />
                        <span className="text-sm font-medium text-text">Google OAuth</span>
                      </div>
                    )}

                    {formData.connector_type === ConnectorType.GOOGLE_DRIVE && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Shared folder or shared drive link <span className="text-xs text-muted">(or use an ID below)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.shared_folder_link || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: {
                                  ...formData.sync_config,
                                  shared_folder_link: e.target.value,
                                },
                              })
                            }
                            placeholder="https://drive.google.com/drive/folders/..."
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Folder ID <span className="text-xs text-muted">(optional if link provided)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.folder_id || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, folder_id: e.target.value },
                              })
                            }
                            placeholder="1abc…"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Shared drive ID <span className="text-xs text-muted">(optional; lists the drive root)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.shared_drive_id || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: {
                                  ...formData.sync_config,
                                  shared_drive_id: e.target.value,
                                },
                              })
                            }
                            placeholder="Shared drive ID"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>

                        {testResult && (
                          <div
                            className={`p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}
                          >
                            {testResult.success ? (
                              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                            ) : (
                              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                            )}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {formData.connector_type === ConnectorType.GOOGLE_SHEETS && (
                      <>
                        <div className="rounded-xl border border-info/20 bg-info/10 p-3 text-sm text-text">
                          <div className="flex items-start gap-2">
                            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-info" />
                            <div>
                              <p className="font-medium">Contract Automation requires Google Sheets write access.</p>
                              <p className="mt-1 text-muted">
                                Use a token granted <code className="rounded bg-surface px-1 py-0.5">https://www.googleapis.com/auth/spreadsheets</code>.
                                Read-only scopes can test successfully but will fail when updating the revenue model.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Spreadsheet link <span className="text-xs text-muted">(or use an ID below)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.spreadsheet_link || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: {
                                  ...formData.sync_config,
                                  spreadsheet_link: e.target.value,
                                },
                              })
                            }
                            placeholder="https://docs.google.com/spreadsheets/d/..."
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Spreadsheet ID <span className="text-xs text-muted">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.spreadsheet_id || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: { ...formData.sync_config, spreadsheet_id: e.target.value },
                                })
                              }
                              placeholder="Spreadsheet ID"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Max rows
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              value={formData.sync_config?.max_rows || 100}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    max_rows: parseInt(e.target.value, 10) || 100,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Ranges <span className="text-xs text-muted">(optional, comma-separated)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.ranges || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, ranges: e.target.value },
                              })
                            }
                            placeholder="Sheet1!A1:Z100, Revenue!A1:H50"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>
                        {testResult && (
                          <div
                            className={`p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}
                          >
                            {testResult.success ? (
                              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                            ) : (
                              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                            )}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {formData.connector_type === ConnectorType.GMAIL && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Gmail search query
                          </label>
                          <input
                            type="text"
                            value={formData.sync_config?.query || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: { ...formData.sync_config, query: e.target.value },
                              })
                            }
                            placeholder="newer_than:30d"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Label IDs <span className="text-xs text-muted">(optional, comma-separated)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.label_ids || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: { ...formData.sync_config, label_ids: e.target.value },
                                })
                              }
                              placeholder="INBOX, SENT"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Max messages
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="500"
                              value={formData.sync_config?.max_messages || 50}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    max_messages: parseInt(e.target.value, 10) || 50,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>
                        {testResult && (
                          <div
                            className={`p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}
                          >
                            {testResult.success ? (
                              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                            ) : (
                              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                            )}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* ERP specific fields */}
                    {formData.connector_type === ConnectorType.ERP && (
                      <>
                        <div className="rounded-xl border border-border bg-surface-2/40 p-4">
                          <div className="flex items-start gap-3">
                            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted" />
                            <div>
                              <p className="text-sm font-medium text-text">Tenant-wide ERP connection</p>
                              <p className="mt-1 text-xs text-muted">
                                ERPNext is available now. Selected DocTypes or tables sync into erp_analytics for Agent Mesh.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              ERP Provider <span className="text-error">*</span>
                            </label>
                            <select
                              value={formData.sync_config?.provider || 'erpnext'}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    provider: e.target.value,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            >
                              {ERP_PROVIDER_OPTIONS.map((provider) => (
                                <option key={provider.value} value={provider.value}>
                                  {provider.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Company ID <span className="text-xs text-muted">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.erp_company_id}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  erp_company_id: e.target.value,
                                  sync_config: {
                                    ...formData.sync_config,
                                    company_id: e.target.value,
                                  },
                                })
                              }
                              placeholder="Company name in ERPNext"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-surface p-4">
                          <label className="block text-sm font-medium text-text mb-3">
                            ERP analytics source mode <span className="text-error">*</span>
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    [ERP_ANALYTICS_SOURCE_MODE_KEY]: ERP_ANALYTICS_SOURCE_MODES.API,
                                  },
                                })
                              }
                              className={`rounded-xl border p-3 text-left transition-colors ${
                                erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.API
                                  ? 'border-brand bg-brand/5 text-text'
                                  : 'border-border bg-surface-2/40 text-text hover:bg-surface-2'
                              }`}
                              aria-pressed={erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.API}
                            >
                              <span className="block text-sm font-medium">API mode</span>
                              <span className="mt-1 block text-xs text-muted">
                                Default. Use ERPNext API credentials and selected DocTypes or tables.
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    [ERP_ANALYTICS_SOURCE_MODE_KEY]: ERP_ANALYTICS_SOURCE_MODES.MARIADB,
                                  },
                                })
                              }
                              className={`rounded-xl border p-3 text-left transition-colors ${
                                erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB
                                  ? 'border-brand bg-brand/5 text-text'
                                  : 'border-border bg-surface-2/40 text-text hover:bg-surface-2'
                              }`}
                              aria-pressed={erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB}
                            >
                              <span className="block text-sm font-medium">MariaDB read-replica</span>
                              <span className="mt-1 block text-xs text-muted">
                                Advanced. Add read-only MariaDB credentials for direct analytics ingestion.
                              </span>
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Base URL <span className="text-error">*</span>
                          </label>
                          <input
                            type="url"
                            value={formData.erp_base_url}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                erp_base_url: e.target.value,
                                sync_config: {
                                  ...formData.sync_config,
                                  base_url: e.target.value,
                                },
                              })
                            }
                            placeholder="https://erp.example.com"
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              API Key <span className="text-error">*</span>
                            </label>
                            <input
                              type="password"
                              value={formData.api_key}
                              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                              placeholder="ERP API key"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              API Secret <span className="text-error">*</span>
                            </label>
                            <input
                              type="password"
                              value={formData.erp_api_secret}
                              onChange={(e) => setFormData({ ...formData, erp_api_secret: e.target.value })}
                              placeholder="ERP API secret"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                          </div>
                        </div>

                        {erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  MariaDB Host <span className="text-error">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={formData.erp_mariadb_host}
                                  onChange={(e) => setFormData({ ...formData, erp_mariadb_host: e.target.value })}
                                  placeholder="erpnext-db.internal"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  MariaDB Port
                                </label>
                                <input
                                  type="text"
                                  value={formData.erp_mariadb_port}
                                  onChange={(e) => setFormData({ ...formData, erp_mariadb_port: e.target.value })}
                                  placeholder="3306"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  MariaDB Database <span className="text-error">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={formData.erp_mariadb_database}
                                  onChange={(e) => setFormData({ ...formData, erp_mariadb_database: e.target.value })}
                                  placeholder="erpnext_production"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  MariaDB User <span className="text-error">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={formData.erp_mariadb_user}
                                  onChange={(e) => setFormData({ ...formData, erp_mariadb_user: e.target.value })}
                                  placeholder="analytics_reader"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-text mb-2">
                                  MariaDB Password <span className="text-error">*</span>
                                </label>
                                <input
                                  type="password"
                                  value={formData.erp_mariadb_password}
                                  onChange={(e) => setFormData({ ...formData, erp_mariadb_password: e.target.value })}
                                  placeholder="MariaDB password"
                                  className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Selected ERP DocTypes / tables JSON <span className="text-error">*</span>
                          </label>
                          <textarea
                            value={formData.erp_selected_tables_json}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                erp_selected_tables_json: e.target.value,
                              })
                            }
                            placeholder={'[\n  {"source_doctype": "Travel Booking", "source_table": "tabTravel Booking", "target_table": "travel_booking"}\n]'}
                            rows={7}
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors font-mono resize-y"
                          />
                          <p className="mt-1 text-xs text-muted">
                            These selections define the DocTypes or source tables that sync into erp_analytics for Agent Mesh.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Max records per API preview
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              value={
                                formData.sync_config?.[ERP_API_PREVIEW_MAX_RECORDS_KEY] || 100
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    [ERP_API_PREVIEW_MAX_RECORDS_KEY]:
                                      parseInt(e.target.value, 10) || 100,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>

                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={
                                !formData.api_key ||
                                !formData.erp_api_secret ||
                                !formData.erp_base_url ||
                                isTesting
                              }
                              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test ERP
                            </button>
                          </div>
                        </div>

                        {testResult && (
                          <div
                            className={`p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}
                          >
                            {testResult.success ? (
                              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                            ) : (
                              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                            )}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* People Data Labs specific fields */}
                    {formData.connector_type === ConnectorType.PEOPLE_DATA_LABS && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            API Key <span className="text-error">*</span>
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={formData.api_key}
                              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                              placeholder="Enter your PDL API key"
                              className="flex-1 px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleTestConnection}
                              disabled={!formData.api_key || isTesting}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border bg-surface-2 text-text border-border hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTesting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                              Test
                            </button>
                          </div>
                          {testResult && (
                            <div className={`mt-2 p-3 rounded-xl flex items-start gap-2 text-sm ${
                              testResult.success ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                            }`}>
                              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />}
                              <span>{testResult.message}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-text flex items-center gap-1">
                              Search Query (JSON)
                              <Tooltip content="PDL API search query in JSON format" position="top">
                                <InformationCircleIcon className="w-4 h-4 text-muted" />
                              </Tooltip>
                            </label>
                            <div className="flex gap-2">
                              {Object.keys(PDL_QUERY_TEMPLATES).map((key) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => applyQueryTemplate(key as keyof typeof PDL_QUERY_TEMPLATES)}
                                  className="text-xs text-brand hover:text-brand-strong hover:underline"
                                >
                                  {key === 'empty' ? 'Clear' : key.replace(/_/g, ' ')}
                                </button>
                              ))}
                            </div>
                          </div>
                          <textarea
                            value={queryJson}
                            onChange={(e) => setQueryJson(e.target.value)}
                            placeholder='{"job_title": ["software engineer"], "location_name": "United States"}'
                            className="w-full px-4 py-3 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-none font-mono"
                            rows={5}
                          />
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={handleEstimateCost}
                              disabled={!formData.api_key || !queryJson || isEstimating}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border bg-surface-2 text-muted border-border hover:bg-surface-3 hover:text-text disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isEstimating ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <CurrencyDollarIcon className="w-3.5 h-3.5" />}
                              Estimate Cost
                            </button>
                            {costEstimate && (
                              <span className="text-xs text-muted">
                                ~{costEstimate.estimated_record_count} records • ${costEstimate.estimated_cost.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Connection availability */}
                    <div className="rounded-xl border border-border bg-surface-2/40 p-4 space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-text">Connection Availability</h3>
                        <p className="mt-1 text-xs text-muted">
                          Define where this saved connection should be reused and which permissions are needed to use it.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Connection Usage
                          </label>
                          <select
                            value={formData.sync_config?.connection_purpose || 'data_source'}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sync_config: {
                                  ...formData.sync_config,
                                  connection_purpose: e.target.value,
                                  use_for_tenant_storage: e.target.value === 'tenant_object_storage',
                                  scope_type:
                                    e.target.value === 'tenant_object_storage'
                                      ? 'tenant'
                                      : formData.sync_config?.scope_type || 'tenant',
                                  scope_target:
                                    e.target.value === 'tenant_object_storage'
                                      ? ''
                                      : formData.sync_config?.scope_target || '',
                                },
                              })
                            }
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                          >
                            <option value="data_source">Applet / workflow data source</option>
                            {formData.connector_type === ConnectorType.S3 && (
                              <option value="tenant_object_storage">Tenant default object storage</option>
                            )}
                          </select>
                        </div>

                        {formData.sync_config?.connection_purpose !== 'tenant_object_storage' && (
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Scope
                            </label>
                            <select
                              value={formData.sync_config?.scope_type || 'tenant'}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    scope_type: e.target.value,
                                  },
                                })
                              }
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            >
                              <option value="tenant">Tenant-wide</option>
                              <option value="applet">Applet-specific</option>
                            </select>
                          </div>
                        )}
                      </div>

                      {formData.sync_config?.connection_purpose !== 'tenant_object_storage' &&
                        formData.sync_config?.scope_type === 'applet' && (
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Applet Key <span className="text-error">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_config?.scope_target || ''}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sync_config: {
                                    ...formData.sync_config,
                                    scope_target: e.target.value,
                                  },
                                })
                              }
                              placeholder="ai-console, talent-intelligence, prompt-management"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        )}

                      <div>
                        <label className="block text-sm font-medium text-text mb-2">
                          Required Permissions <span className="text-xs text-muted">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={(formData.sync_config?.required_permissions || []).join(', ')}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              sync_config: {
                                ...formData.sync_config,
                                required_permissions: e.target.value
                                  .split(',')
                                  .map((permission) => permission.trim())
                                  .filter(Boolean),
                              },
                            })
                          }
                          placeholder="documents:write, ai_console:access"
                          className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                        />
                      </div>
                    </div>

                    {/* Sync Mode */}
                    {selectedConnector.supportsManualSync && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">
                            Sync Mode
                          </label>
                          <select
                            value={formData.sync_mode}
                            onChange={(e) => setFormData({ ...formData, sync_mode: e.target.value as SyncMode })}
                            className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:12px] bg-[position:right_1rem_center] bg-no-repeat pr-10"
                          >
                            <option value={SyncMode.FULL_REFRESH}>Full Refresh</option>
                            {!isERP && <option value={SyncMode.INCREMENTAL}>Incremental</option>}
                          </select>
                        </div>

                        {selectedConnector.supportsSchedule && (
                          <div>
                            <label className="block text-sm font-medium text-text mb-2">
                              Schedule <span className="text-xs text-muted">(optional)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.sync_schedule || ''}
                              onChange={(e) => setFormData({ ...formData, sync_schedule: e.target.value })}
                              placeholder="e.g., 0 2 * * *"
                              className="w-full px-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-text mb-2">
                        Description <span className="text-xs text-muted">(optional)</span>
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe the purpose of this connection"
                        className="w-full px-4 py-3 border border-border rounded-xl bg-surface text-text text-sm placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-none"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-surface-2/50">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 border bg-surface text-muted border-border hover:bg-surface-2 hover:text-text"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isCreating ||
                        isStartingOauth ||
                        (!isGranola && formData.connector_type !== ConnectorType.BIGQUERY && !formData.connector_name) ||
                        (formData.connector_type === ConnectorType.BIGQUERY &&
                          (!formData.connector_name ||
                            !formData.bigquery_project_id ||
                            !formData.bigquery_client_id ||
                            !formData.bigquery_client_secret ||
                            !(formData.bigquery_redirect_uri || bigQueryCallbackUri))) ||
                        (formData.connector_type === ConnectorType.S3 &&
                          (!formData.access_key_id ||
                            !formData.secret_access_key ||
                            !formData.sync_config?.bucket)) ||
                        (formData.connector_type === ConnectorType.PEOPLE_DATA_LABS && !formData.api_key) ||
                        (formData.connector_type === ConnectorType.GREENHOUSE && !formData.api_key) ||
                        (formData.connector_type === ConnectorType.HUBSPOT && !formData.api_key) ||
                        (formData.connector_type === ConnectorType.OPERATING &&
                          (!formData.api_key ||
                            !String(formData.sync_config?.base_url || '').trim())) ||
                        (formData.connector_type === ConnectorType.FATHOM && !formData.api_key) ||
                        (formData.connector_type === ConnectorType.SLACK && !formData.api_key) ||
                        (isGoogleOAuthConnector(formData.connector_type) && !hasGoogleSourceDetails) ||
                        (formData.connector_type === ConnectorType.FILESYSTEM && !formData.sync_config?.directory_path) ||
                        (formData.connector_type === ConnectorType.ERP &&
                          (!formData.api_key ||
                            !formData.erp_api_secret ||
                            !formData.erp_base_url ||
                            !formData.erp_selected_tables_json.trim() ||
                            (erpAnalyticsSourceMode === ERP_ANALYTICS_SOURCE_MODES.MARIADB &&
                              missingERPAnalyticsCredential(formData)))) ||
                        (formData.connector_type === ConnectorType.SNOWFLAKE &&
                          (!formData.snowflake_account ||
                            !formData.snowflake_username ||
                            !formData.snowflake_password ||
                            !formData.snowflake_database)) ||
                        (formData.connector_type === ConnectorType.POSTGRES &&
                          !formData.postgres_secret_id.trim() &&
                          (!formData.postgres_host ||
                            !formData.postgres_database ||
                            !formData.postgres_username ||
                            !formData.postgres_password)) ||
                        (formData.connector_type === ConnectorType.MARIADB &&
                          (!formData.mariadb_host ||
                            !formData.mariadb_database ||
                            !formData.mariadb_username ||
                            !formData.mariadb_password)) ||
                        (formData.sync_config?.connection_purpose !== 'tenant_object_storage' &&
                          formData.sync_config?.scope_type === 'applet' &&
                          !String(formData.sync_config?.scope_target || '').trim())
                      }
                      className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-xl transition-all duration-200 bg-brand text-on-brand hover:bg-brand-strong disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {(isCreating || isStartingOauth) && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                      {isGranola
                        ? (isStartingOauth ? 'Opening OAuth...' : 'Connect with Granola')
                        : isGoogleOAuthConnector(formData.connector_type)
                          ? (isStartingOauth ? 'Opening OAuth...' : 'Connect Google')
                        : formData.connector_type === ConnectorType.BIGQUERY
                          ? (isCreating ? 'Saving...' : 'Save connection')
                        : (isCreating ? 'Creating...' : 'Create Connection')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
